import React from 'react';

import { Header, Text, makeUclComponent } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { useStoreContext } from 'features/store';
import {
  formatTime,
  getNextDayOpeningHours,
  getSelectedStoreCurrentDate,
} from 'features/store/utils';
import { StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { Helmet } from 'components/helmet';
import { LayoutContainer } from 'components/layout/styled';
import Picture from 'components/picture';
import { useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useFeatureLoyaltyUi } from 'hooks/use-feature-loyalty-ui/use-feature-loyalty-ui';
import { routes } from 'navigation/routing';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import { spacing } from 'styles/constants';

const NotAvailableContainer = LayoutContainer.withConfig({
  width: '$90',
  alignItems: 'center',
  alignSelf: 'center',
});

const H1 = Header.withConfig({
  marginLeft: '$10',
  textAlign: 'center',
  marginRight: '$10',
  marginTop: '$3',
  marginBottom: '$3',
  fontSize: 'xl',
});

const P = Text.withConfig({
  fontSize: 'md',
  textAlign: 'center',
  marginBottom: spacing.$3_5,
  paddingX: '$1',
  width: '$80',
});

const Button = makeUclComponent(ActionButton).withConfig({
  width: '$80',
  marginTop: '$4',
  marginBottom: '$4',
});

const localNotAvailableImage = require('assets/images/loyalty/royal-perks-crowns.png');

const LoyaltyNotAvailableAtThisRestaurant: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isStoreClosed, store } = useStoreContext();
  const { isDelivery } = useServiceModeContext();

  const { featureLoyaltyUi } = useFeatureLoyaltyUi();

  const logo = featureLoyaltyUi?.rewardsLogo?.locale;

  let title = t('loyaltyNotAvailableAtThisRestaurantTitle');
  let description = t('loyaltyNotAvailableAtThisRestaurantDescription');

  if (isStoreClosed) {
    const storeCurrentlyClosedMessage = t('storeCurrentlyClosed');

    title = storeCurrentlyClosedMessage;

    const { diningRoomHours, deliveryHours } = store;
    const operatingHours = isDelivery ? deliveryHours : diningRoomHours;

    if (operatingHours) {
      const formattedTomorrowOpeningHours = formatTime(
        getNextDayOpeningHours(operatingHours, getSelectedStoreCurrentDate(store.timezone || ''))
      );

      description = t('oopsComeBackTomorrow', { storeTime: formattedTomorrowOpeningHours });
    } else {
      description = storeCurrentlyClosedMessage;
    }
  }

  const firstRedirectPath = routes.menu;
  const firstButtonText = t('loyaltyNotAvailableAtThisRestaurantPrimaryCta');

  useLogUserErrorMessage({ message: title });

  return (
    <NotAvailableContainer>
      <Helmet title={t('loyaltyNotAvailableAtThisRestaurant')} />
      <Picture
        image={logo}
        alt={t('royalPerks')}
        objectFitContain
        lazy={false}
        lqOff
        width="$64"
        height="$32"
      />
      <Image
        source={localNotAvailableImage}
        accessibilityLabel="Royal perks crown logos scattered around"
        contentFit="contain"
        style={styles.crownsImage}
      />
      <H1>{title}</H1>
      <P>{description}</P>
      <Button to={firstRedirectPath}>{firstButtonText}</Button>
    </NotAvailableContainer>
  );
};

const styles = StyleSheet.create({
  crownsImage: { width: 192, height: 192 },
});

export default LoyaltyNotAvailableAtThisRestaurant;
